import React from 'react';
import { useParams } from 'react-router-dom';
import { MapPinIcon, BriefcaseIcon, LayersIcon, BanknoteIcon, HashIcon, CalendarIcon, ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { jobs, SAMPLE_JOBS_NOTICE } from '../../data/jobs';
import { industries } from '../../data/industries';
import { formatDate } from '../../utils/format';
import { PageHero } from '../../components/ui/PageHero';
import { SampleTag } from '../../components/ui/SampleTag';
import { Button } from '../../components/ui/Button';
import { JobCard } from '../../components/jobs/JobCard';
import { ApplicationForm } from '../../components/forms/ApplicationForm';
import { NotFound } from '../NotFound';
export function JobDetail() {
  const {
    id
  } = useParams();
  const job = jobs.find(j => j.id === id);
  usePageMeta(job ? `${job.title} (Sample)` : 'Role not found', job ? `${job.summary} Sample listing — ${job.location}.` : 'This role could not be found.');
  if (!job) return <NotFound data-id="element-643" data-mp="1fh6hx4h854lc" />;
  const industry = industries.find(i => i.slug === job.industry);
  const similar = jobs.filter(j => j.id !== job.id && (j.industry === job.industry || j.seniority === job.seniority)).concat(jobs.filter(j => j.id !== job.id)).filter((j, i, arr) => arr.findIndex(x => x.id === j.id) === i).slice(0, 3);
  const facts = [{
    icon: HashIcon,
    label: 'Reference',
    value: job.ref
  }, {
    icon: MapPinIcon,
    label: 'Location',
    value: `${job.location} · ${job.arrangement}`
  }, {
    icon: BriefcaseIcon,
    label: 'Employment type',
    value: job.type
  }, {
    icon: LayersIcon,
    label: 'Seniority',
    value: job.seniority
  }, {
    icon: BanknoteIcon,
    label: 'Salary',
    value: job.salary ?? 'Not disclosed'
  }, {
    icon: CalendarIcon,
    label: 'Published',
    value: formatDate(job.posted)
  }];
  const sections = [{
    title: 'Key responsibilities',
    items: job.responsibilities
  }, {
    title: 'Qualifications and experience',
    items: job.qualifications
  }, {
    title: 'Required competencies',
    items: job.competencies
  }];
  return <>
      <PageHero crumbs={[{
      label: 'Find Jobs',
      to: '/jobs'
    }, {
      label: job.title
    }]} title={job.title} intro={`${industry?.name ?? ''} · ${job.location}`} actions={<>
            <Button to="#apply" size="lg" arrow data-id="element-645" data-mp="25s8n0zw6p2lt">
              Apply Now
            </Button>
            <SampleTag className="self-center" data-id="element-646" data-mp="21q5as91oxtzh" />
          </>} data-id="element-644" data-mp="2fgqj53tiqlj4" />

      <section className="bg-ivory py-16 md:py-20" data-id="element-647" data-mp="1wqsfr10o1glt">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-648" data-mp="1wp7ih3myvjk9">
          <article className="lg:col-span-8" data-id="element-649" data-mp="1rqw90u0l3b2k">
            <div className="rounded-md border border-gold/40 bg-gold-100 px-4 py-3 text-sm text-gold-700" data-id="element-650" data-mp="1rts6kh8irxqh">{SAMPLE_JOBS_NOTICE}</div>
            <h2 className="mt-10 font-display text-3xl font-semibold text-ink" data-id="element-651" data-mp="3m5gn2nueg8cz">Role overview</h2>
            <p className="mt-4 text-lg leading-relaxed text-muted" data-id="element-652" data-mp="3h6vn8pj27qp2">{job.overview}</p>
            {sections.map(s => <div key={s.title} className="mt-10" data-id="element-653" data-mp="3pofsf9yobqpt">
                <h2 className="font-display text-3xl font-semibold text-ink" data-id="element-654" data-mp="3pltmferflj9p">{s.title}</h2>
                <ul className="mt-4 space-y-3" data-id="element-655" data-mp="3tn53ux05iy6t">
                  {s.items.map(item => <li key={item} className="flex gap-3 leading-relaxed text-muted" data-id="element-656" data-mp="2bc0tmvm3bdk">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" data-id="element-657" data-mp="7959cmke0hh0" />
                      {item}
                    </li>)}
                </ul>
              </div>)}
            <div className="mt-10" data-id="element-658" data-mp="3as6tk9ip3fdx">
              <h2 className="font-display text-3xl font-semibold text-ink" data-id="element-659" data-mp="3fp700ljgik5k">Benefits</h2>
              {job.benefits ? <ul className="mt-4 space-y-2 text-muted" data-id="element-660" data-mp="3kq4jd1g7dk8n">
                  {job.benefits.map(b => <li key={b} data-id="element-661" data-mp="uypvfebu7uui">{b}</li>)}
                </ul> : <p className="mt-4 text-muted" data-id="element-662" data-mp="zwrdbvxj1o4e">Benefits will be confirmed with the client organisation and shared with shortlisted candidates.</p>}
            </div>
            <div className="mt-10" data-id="element-663" data-mp="23x7o0778ofek">
              <h2 className="font-display text-3xl font-semibold text-ink" data-id="element-664" data-mp="23y95ateuly4q">How to apply</h2>
              <p className="mt-4 leading-relaxed text-muted" data-id="element-665" data-mp="2hx8ovmni7mu1">
                Complete the application form below and attach your CV. A consultant reviews every application against the brief and will contact
                you if there is a potential match. Submitting an application does not guarantee an interview.
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4" data-id="element-666" data-mp="30l5devkkfqeh">
            <div className="sticky top-28 space-y-5" data-id="element-667" data-mp="30o9rqlrqw2iv">
              <div className="rounded-xl border border-line bg-white p-6" data-id="element-668" data-mp="2wm4uze602ps0">
                <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-669" data-mp="3ajtiyuvd6d1a">Role summary</h2>
                <dl className="mt-5 space-y-4" data-id="element-670" data-mp="35m1phwf2miuz">
                  {facts.map(f => {
                  const Icon = f.icon;
                  return <div key={f.label} className="flex gap-3" data-id="element-671" data-mp="3p58tqy4nkudp">
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" data-id="element-672" data-mp="3u53jgycozdx6" />
                        <div data-id="element-673" data-mp="3f8ci4jjgjs4x">
                          <dt className="text-xs text-muted" data-id="element-674" data-mp="3fbi4vs4m6hxh">{f.label}</dt>
                          <dd className="text-sm text-ink" data-id="element-675" data-mp="3k6npmnma5qjz">{f.value}</dd>
                        </div>
                      </div>;
                })}
                </dl>
                <Button to="#apply" variant="navy" className="mt-6 w-full" arrow data-id="element-676" data-mp="3b8c1cs8vz4up">
                  Apply Now
                </Button>
              </div>
              <div className="flex gap-3 rounded-xl bg-midnight p-6 text-sm text-white/80" data-id="element-677" data-mp="31hhdxkjwlz7o">
                <ShieldCheckIcon className="h-5 w-5 shrink-0 text-gold-300" aria-hidden="true" data-id="element-678" data-mp="2nhy4mofcwhts" />
                <p data-id="element-679" data-mp="2rjjq2y5yi4fl">Your application is confidential. We never share your details with an employer without your explicit permission.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section id="apply" className="scroll-mt-24 bg-white py-20" data-id="element-680" data-mp="t9m0icwt1phe">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-681" data-mp="t8avyavjcdp8">
          <div className="lg:col-span-4" data-id="element-682" data-mp="obkgnprk1m4v">
            <h2 className="font-display text-4xl font-semibold text-ink" data-id="element-683" data-mp="jby6jp70wm0a">Apply for this role</h2>
            <p className="mt-3 text-muted" data-id="element-684" data-mp="1gzonielv4595">{job.title}</p>
            <div className="mt-8 rounded-lg bg-ivory p-5 text-sm leading-relaxed text-muted" data-id="element-685" data-mp="1gz6k8y0s8qrq">
              <h3 className="font-medium text-ink" data-id="element-686" data-mp="1c1wutide8f3w">Candidate privacy notice</h3>
              <p className="mt-2" data-id="element-687" data-mp="170hchy2lomp7">
                We use the information you provide only to assess your suitability for this and, if you choose, similar roles. You can ask us to
                access, correct or delete your data at any time. See our Candidate Data Privacy notice for details.
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-line p-6 md:p-10 lg:col-span-8" data-id="element-688" data-mp="3ns91r75mfkdz">
            <ApplicationForm job={job} data-id="element-689" data-mp="239yilek4luca" />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-20" data-id="element-690" data-mp="27aibu71kwdt5">
        <div className="container-page" data-id="element-691" data-mp="2c8sc2w25u4lm">
          <h2 className="font-display text-4xl font-semibold text-ink" data-id="element-692" data-mp="2c7qwqfe42vy3">Similar opportunities</h2>
          <ul className="mt-10 grid gap-6 md:grid-cols-3" data-id="element-693" data-mp="2h0k1h39ky4a6">
            {similar.map(j => <li key={j.id} data-id="element-694" data-mp="2lxjy6nkjhuom">
                <JobCard job={j} data-id="element-695" data-mp="2lybvq3xp62hj" />
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}